import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useIdentity } from '../state/useIdentity';
import { isMockGateway, identityGateway } from '../services';
import { ApiError, type LoginOutcome, type LoginResponse } from '../types';
import { AuthShellLayout } from '../components/AuthShellLayout';
import type { Slide } from '../components/HeroCarousel';
import { TextField } from '../../../shared/components/TextField';
import { PasswordField } from '../../../shared/components/PasswordField';
import { Button } from '../../../shared/components/Button';
import { InlineAlert } from '../../../shared/components/InlineAlert';
import { friendlyErrorMessage, SESSION_EXPIRED_MESSAGE } from '../../../shared/friendlyError';
import { isSafeReturnTo } from '../../../shared/isSafeReturnTo';

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  email?: string;
  password?: string;
}

interface LoginPageProps {
  /** Set by App.tsx when the last `refresh()` failed because a previously-established session actually
   * expired/was revoked (see IdentityContext.ts's own doc comment on `sessionExpired`) — distinct from a
   * visitor who was simply never signed in, who never sees this. */
  sessionExpired?: boolean;
  /** EPN-36 Partner Sign In overrides — every default below reproduces Admin Sign In's current behavior
   * exactly; only PartnerLoginPage passes anything else. The credential/lockout/MFA logic itself (this
   * component's real auth surface) is identical either way — these props only change the shell's hero
   * imagery and the two footer links. */
  heroSlides?: Slide[] | undefined;
  /** The Locked view's own "Back to Sign In" link — defaults to this same page's own route. */
  signInHref?: string;
  forgotPasswordHref?: string;
  signUpHref?: string;
  signUpLabel?: string;
  /** Admin Sign In shows a second "Signing up as a Partner?" line beneath its own Sign Up link; Partner
   * Sign In's own page already IS that destination, so it suppresses this to avoid a redundant/circular
   * link to itself. */
  showPartnerSignUpLink?: boolean;
  /** EPN-36 — if provided, called instead of the default post-login navigation once `login()`/
   * `completeMfaVerification()` resolves to `'success'`; receives the raw `LoginResponse` (its
   * `memberships` carry `isPartnerChannel`/`companyIsActive`). When provided, this REPLACES the default
   * `navigateAfterCompletedLogin()` call entirely — PartnerLoginPage needs to navigate explicitly in every
   * branch (including "proceed into the app"), since `/partner-login` is itself a PUBLIC_ROUTE_PREFIXES
   * entry in App.tsx and so never falls through to the authenticated route tree on its own the way plain
   * `/login` does (see PartnerLoginPage's own doc comment). Admin Sign In never passes this — its behavior
   * is completely unchanged. */
  onLoginSuccess?: (result: LoginResponse) => void;
}

type MfaStage =
  | { step: 'selectMethod' }
  | { step: 'enterCode'; method: string; codeSent: boolean };

interface MfaState {
  challengeToken: string;
  availableMethods: string[];
  stage: MfaStage;
}

export function LoginPage({
  sessionExpired = false,
  heroSlides,
  signInHref = '/login',
  forgotPasswordHref = '/forgot-password',
  signUpHref = '/signup',
  signUpLabel = 'Sign Up',
  showPartnerSignUpLink = true,
  onLoginSuccess,
}: LoginPageProps) {
  const { login, completeMfaVerification, requestUnlockAfterLockout, loading } = useLoginPageIdentity();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);

  // Local, in-place view state for the two non-success outcomes — deliberately never routed to their own
  // URL. A challengeToken is a short-lived, one-time credential; putting it in a bookmarkable/shareable
  // URL would be a real security regression, not just a style choice.
  const [mfa, setMfa] = useState<MfaState | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [rememberDevice, setRememberDevice] = useState(false);
  const [mfaError, setMfaError] = useState<string | null>(null);
  const [mfaBusy, setMfaBusy] = useState(false);

  const [lockedUntil, setLockedUntil] = useState<string | null>(null);
  const [unlockRequested, setUnlockRequested] = useState(false);
  const [unlockBusy, setUnlockBusy] = useState(false);

  function navigateAfterCompletedLogin() {
    const returnTo = searchParams.get('returnTo');
    if (isSafeReturnTo(returnTo)) {
      navigate(returnTo, { replace: true });
    }
    // An invalid/absent returnTo falls through to whatever App.tsx's own routing already does by default
    // (currently: the authenticated route table's "/login" entry redirects to "/dashboard") — nothing
    // further to do here.
  }

  function handleOutcome(outcome: LoginOutcome) {
    if (outcome.kind === 'success') {
      if (onLoginSuccess) {
        onLoginSuccess({ userId: outcome.userId, email: outcome.email, memberships: outcome.memberships });
      } else {
        navigateAfterCompletedLogin();
      }
      return;
    }
    if (outcome.kind === 'locked') {
      setLockedUntil(outcome.lockoutEndUtc);
      return;
    }
    // 'mfaRequired'. Per the backend contract (see AuthEndpoints' /login handler and MfaEndpoints' own
    // doc comments), NEITHER method has a code ready yet at this point — Authenticator never needs one
    // delivered, and Email's code is only ever sent by an explicit /mfa/challenge call, never
    // automatically at /login. A single available method still needs that same explicit step unless it
    // is Authenticator.
    if (outcome.availableMethods.length === 1) {
      const [onlyMethod] = outcome.availableMethods;
      setMfa({
        challengeToken: outcome.challengeToken,
        availableMethods: outcome.availableMethods,
        stage: { step: 'enterCode', method: onlyMethod ?? '', codeSent: false },
      });
      if (onlyMethod && onlyMethod !== 'Authenticator') {
        void sendMfaCode(outcome.challengeToken, onlyMethod);
      }
    } else {
      setMfa({ challengeToken: outcome.challengeToken, availableMethods: outcome.availableMethods, stage: { step: 'selectMethod' } });
    }
  }

  async function sendMfaCode(challengeToken: string, method: string) {
    setMfaError(null);
    setMfaBusy(true);
    try {
      await identityGateway.mfaChallenge(challengeToken, method);
      setMfa((prev) => (prev ? { ...prev, stage: { step: 'enterCode', method, codeSent: true } } : prev));
    } catch (err) {
      setMfaError(friendlyErrorMessage(err, 'Could not send a verification code.'));
    } finally {
      setMfaBusy(false);
    }
  }

  function selectMfaMethod(method: string) {
    setMfaCode('');
    setMfaError(null);
    if (!mfa) return;
    if (method === 'Authenticator') {
      setMfa({ ...mfa, stage: { step: 'enterCode', method, codeSent: false } });
      return;
    }
    setMfa({ ...mfa, stage: { step: 'enterCode', method, codeSent: false } });
    void sendMfaCode(mfa.challengeToken, method);
  }

  function backToMethodSelection() {
    setMfaCode('');
    setMfaError(null);
    setMfa((prev) => (prev ? { ...prev, stage: { step: 'selectMethod' } } : prev));
  }

  async function handleMfaSubmit(e: FormEvent) {
    e.preventDefault();
    if (!mfa || mfa.stage.step !== 'enterCode' || mfaBusy) return;
    setMfaError(null);
    setMfaBusy(true);
    try {
      const result = await completeMfaVerification(mfa.challengeToken, mfa.stage.method, mfaCode, rememberDevice);
      handleOutcome({ kind: 'success', ...result });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400 && err.messageIsServerAuthored) {
        setMfaError(err.message);
      } else {
        setMfaError(friendlyErrorMessage(err, "We couldn't verify that code right now. Please try again."));
      }
    } finally {
      setMfaBusy(false);
    }
  }

  async function handleRequestUnlock() {
    setUnlockBusy(true);
    try {
      await requestUnlockAfterLockout(email);
    } finally {
      setUnlockBusy(false);
      setUnlockRequested(true);
    }
  }

  function validate(trimmedEmail: string): boolean {
    const errors: FieldErrors = {};
    if (!trimmedEmail) {
      errors.email = 'Enter your email address.';
    } else if (!EMAIL_FORMAT.test(trimmedEmail)) {
      errors.email = 'Enter a valid email address.';
    }
    if (!password) {
      errors.password = 'Enter your password.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (loading) {
      // Belt-and-braces: the submit button is already disabled while loading, but pressing Enter in a
      // field submits the form directly and bypasses a disabled button entirely.
      return;
    }

    setFormError(null);
    const trimmedEmail = email.trim();
    if (!validate(trimmedEmail)) {
      return;
    }

    try {
      const outcome = await login(trimmedEmail, password, rememberMe);
      handleOutcome(outcome);
    } catch (err) {
      // A 401 here means invalid credentials, never an expired session — this is the first request of a
      // brand new session, so there is nothing to have expired yet. Deliberately not using the shared
      // friendlyErrorMessage() helper: its 401 mapping ("Your session has expired…") is correct for an
      // already-authenticated call elsewhere in the app but would be wrong on this page.
      if (err instanceof ApiError && err.status === 401) {
        setFormError('The email or password you entered is incorrect.');
      } else if (err instanceof ApiError) {
        setFormError("We couldn't sign you in right now. Please try again.");
      } else {
        setFormError("We couldn't connect to Escochex. Check your connection and try again.");
      }
    }
  }

  if (lockedUntil) {
    return (
      <AuthShellLayout heroSlides={heroSlides}>
        <div className="auth-shell__form-slot" data-testid="account-locked-view">
          <h1>Account Locked</h1>
          <p className="muted">
            Too many incorrect sign-in attempts. Your account is locked until{' '}
            <strong>{new Date(lockedUntil).toLocaleString()}</strong>.
          </p>
          {unlockRequested ? (
            <InlineAlert variant="success" data-testid="unlock-requested-message">
              If that account is locked, we&rsquo;ve sent instructions to unlock it.
            </InlineAlert>
          ) : (
            <Button type="button" loading={unlockBusy} loadingLabel="Sending…" onClick={() => void handleRequestUnlock()} data-testid="unlock-via-email-button">
              Unlock via email
            </Button>
          )}
          <p className="auth-shell__secondary-action">
            <Link to={signInHref} className="auth-shell__inline-link" onClick={() => setLockedUntil(null)}>
              Back to Sign In
            </Link>
          </p>
        </div>
      </AuthShellLayout>
    );
  }

  if (mfa) {
    const enterCodeStage = mfa.stage.step === 'enterCode' ? mfa.stage : null;

    return (
      <AuthShellLayout heroSlides={heroSlides}>
        <div className="auth-shell__form-slot" data-testid="mfa-challenge-view">
          <h1>Verify It&rsquo;s You</h1>

          {mfa.stage.step === 'selectMethod' && (
            <div data-testid="mfa-method-select">
              <p className="muted">Choose how you&rsquo;d like to verify your identity.</p>
              {mfa.availableMethods.map((method) => (
                <Button key={method} type="button" onClick={() => selectMfaMethod(method)} data-testid={`mfa-method-${method.toLowerCase()}`}>
                  {method === 'Authenticator' ? 'Authenticator App' : `${method} Code`}
                </Button>
              ))}
            </div>
          )}

          {enterCodeStage && (
            <form onSubmit={handleMfaSubmit} noValidate>
              <p className="muted">
                {enterCodeStage.method === 'Authenticator'
                  ? 'Enter the code from your authenticator app.'
                  : `We sent a verification code to your email.`}
              </p>

              <TextField
                label="Verification Code"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                data-testid="mfa-code-input"
                autoFocus
              />

              <div className="form-field">
                <label>
                  <input
                    type="checkbox"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                    data-testid="mfa-remember-device-checkbox"
                  />{' '}
                  Remember this device for 30 days
                </label>
              </div>

              {mfaError && (
                <InlineAlert variant="error" data-testid="mfa-error-message">
                  {mfaError}
                </InlineAlert>
              )}

              <div className="auth-shell__submit-row">
                <Button type="submit" loading={mfaBusy} loadingLabel="Verifying…" className="auth-submit-button" data-testid="mfa-verify-button">
                  Verify
                </Button>
              </div>

              <p className="auth-shell__secondary-action">
                {enterCodeStage.method !== 'Authenticator' && (
                  <>
                    <button
                      type="button"
                      className="auth-shell__inline-link auth-shell__link-button"
                      onClick={() => void sendMfaCode(mfa.challengeToken, enterCodeStage.method)}
                      disabled={mfaBusy}
                      data-testid="mfa-resend-button"
                    >
                      Resend code
                    </button>
                    {mfa.availableMethods.length > 1 && ' · '}
                  </>
                )}
                {mfa.availableMethods.length > 1 && (
                  <button type="button" className="auth-shell__inline-link auth-shell__link-button" onClick={backToMethodSelection} data-testid="mfa-try-another-method">
                    Try another method
                  </button>
                )}
              </p>
            </form>
          )}
        </div>
      </AuthShellLayout>
    );
  }

  return (
    <AuthShellLayout heroSlides={heroSlides}>
      <form onSubmit={handleSubmit} noValidate>
        <div className="auth-shell__form-slot">
          <h1>Sign In</h1>
        </div>
        {isMockGateway && <p className="muted">Mock mode — try admin@epn.local / password</p>}

        {sessionExpired && (
          <InlineAlert variant="error" data-testid="session-expired-message">
            {SESSION_EXPIRED_MESSAGE}
          </InlineAlert>
        )}

        <TextField
          label="Email Address"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          errorMessage={fieldErrors.email}
          data-testid="admin-login-email-input"
          autoFocus
        />

        <PasswordField
          label="Password"
          labelExtra={
            <Link to={forgotPasswordHref} className="auth-shell__inline-link">
              Forgot your Password?
            </Link>
          }
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          errorMessage={fieldErrors.password}
          data-testid="admin-login-password-input"
        />

        <div className="form-field">
          <label>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              data-testid="admin-login-remember-me-checkbox"
            />{' '}
            Remember me
          </label>
        </div>

        {formError && (
          <InlineAlert variant="error" data-testid="login-error-message">
            {formError}
          </InlineAlert>
        )}

        <div className="auth-shell__submit-row">
          <Button
            type="submit"
            loading={loading}
            loadingLabel="Signing In…"
            className="auth-submit-button"
            data-testid="admin-login-submit-button"
          >
            Sign In
          </Button>
          <p className="auth-shell__secondary-action">
            {/* Admin Sign Up (EPN-32) is a real, public route as of this checkout — see App.tsx's
                PUBLIC_ROUTE_PREFIXES. signUpHref/signUpLabel default to Admin Sign Up; PartnerLoginPage
                (EPN-36) passes its own /signup/partner + "Sign Up" instead. */}
            Don&rsquo;t have an account?{' '}
            <Link to={signUpHref} className="auth-shell__inline-link">
              {signUpLabel}
            </Link>
          </p>
          {showPartnerSignUpLink && (
            <p className="auth-shell__secondary-action">
              {/* Partner Sign Up (EPN-35) — same PUBLIC_ROUTE_PREFIXES "/signup" prefix, its own route.
                  Suppressed on PartnerLoginPage itself (see showPartnerSignUpLink's own doc comment). */}
              Signing up as a Partner?{' '}
              <Link to="/signup/partner" className="auth-shell__inline-link">
                Register as Partner
              </Link>
            </p>
          )}
        </div>
      </form>
    </AuthShellLayout>
  );
}

/** Thin wrapper around useIdentity() adding the one extra action (requesting an account-unlock email
 * from the Locked view) that isn't part of the shared IdentityContext — unlock-request has no bearing on
 * `me`/session state, so it talks to the gateway directly rather than growing the context for a
 * single-page need. */
function useLoginPageIdentity() {
  const identity = useIdentity();
  async function requestUnlockAfterLockout(emailAddress: string) {
    const trimmed = emailAddress.trim();
    if (!trimmed) return;
    try {
      await identityGateway.requestUnlock(trimmed);
    } catch {
      // Enumeration-safe on the backend (always 200) — a network failure here has no useful recovery
      // beyond letting the user try the standalone /unlock-request page instead, which surfaces its own
      // error state.
    }
  }
  return { ...identity, requestUnlockAfterLockout };
}
